import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { formatApiError } from "../../lib/api";
import { deleteFirmware } from "../../lib/firmwareApi";

export default function FirmwareDeleteDialog({ open, record, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
    inFlight.current = false;
  }, [open, record?.id]);

  if (!open || !record) return null;

  async function confirmDelete() {
    if (inFlight.current) return;
    inFlight.current = true;
    setDeleting(true);
    setError("");
    try {
      const response = await deleteFirmware(record.id);
      toast.success(response?.message || "Firmware record permanently deleted.");
      onDeleted?.(record.id);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      inFlight.current = false;
      setDeleting(false);
    }
  }

  return (
    <Modal
      title="Delete Firmware"
      onClose={onClose}
      busy={deleting}
      size="sm"
      testid="firmware-delete-dialog"
    >
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">
          This Firmware record will be permanently deleted and cannot be restored.
        </p>
        <dl className="grid gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Model</dt>
            <dd className="mt-1 break-words text-sm font-semibold" data-testid="firmware-delete-model">{record.model}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Firmware Version</dt>
            <dd className="mt-1 break-words text-sm font-semibold" data-testid="firmware-delete-version">{record.firmware_version}</dd>
          </div>
        </dl>
        {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={confirmDelete}
            loading={deleting}
            loadingLabel="Deleting…"
            data-testid="firmware-confirm-delete"
          >
            Delete Permanently
          </Button>
        </div>
      </div>
    </Modal>
  );
}
